import { Component, Input, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, ChangeDetectionStrategy, inject } from '@angular/core';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { BaseViewComponent } from '../base-view.component';
import { DatasetActionContainerService } from 'app/main/components/dataset/services/dataset-action-container.service';
import { DatasetNavigatorService } from 'app/main/components/dataset/services/dataset-navigator.service';
import { DatasetRecordService } from 'app/main/components/dataset/services/dataset-record.service';
import { MatStepper } from '@angular/material/stepper';
import { MatTabGroup } from '@angular/material/tabs';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { AssistantService } from 'app/main/components/assistant/assistant.service';
import { AssistantContextService } from 'app/main/components/assistant/assistant-context.service';
import { IAssistantClientAction } from 'app/main/components/assistant/assistant.models';
import { TranslateService } from '@ngx-translate/core';
import { CommonFormStepComponent } from '../common-form-step/step.component';

import { AppService } from 'app/services/app.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { IStepViewConfig, IStepConfig } from 'app/interfaces';
import { StepService } from 'app/main/components/dataset/services/step-state.service';

@Component({
    selector: 'common-step-view-container',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.scss'],
    providers: [StepService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommonStepViewContainerComponent extends BaseViewComponent implements OnInit, OnDestroy{

	private assistantService = inject(AssistantService);
	private assistantContextService = inject(AssistantContextService);
	private assistantAnchorKey: string | null = null;
	private assistantAnchoredArea: string | null = null;
	/** IAssistantAnchorConfig.autoOpen: pannello già aperto da questa scheda (chiuso dall'utente resta chiuso) */
	private assistantAutoOpened = false;
	private translateService = inject(TranslateService);
	/** step form renderizzati: l'assistente ci scrive i valori (fill_form) o li salva (next_step) */
	@ViewChildren(CommonFormStepComponent) formSteps: QueryList<CommonFormStepComponent>;
	
	@Input() stepViewConfig: IStepViewConfig;
	public initStep = false;
	public stepIndex = 0;
	public stepperIndex = 0;
	public isStepLinear = true;
	@ViewChild('stepper', {static: true}) public stepper: MatStepper;
	@ViewChild(MatTabGroup, { static: false }) tabGroup: MatTabGroup;

	public computedSteps: any[] = [];

	public showOverview = true;

	public steps: IStepConfig[];

	public datasetStepViewConfigs: any;

	public subtitle: string;

	/*@Input() 
	set record(record: any){
		this.datasetRS.record.next(record);
	}*/
	get record(): any{ return this.datasetRS.record.value; }

	constructor(
		public appService: AppService,
		public datasetRS: DatasetRecordService,
		public datasetACS: DatasetActionContainerService,
		protected datasetNavigatorService: DatasetNavigatorService,
		protected _fuseSidebarService: FuseSidebarService,
		protected router: Router,
		protected route: ActivatedRoute,
		protected location: Location,
		protected stepService?: StepService,
	){
		super(datasetACS, datasetNavigatorService);
	}

	ngOnInit(): void{
		super.ngOnInit();
		this.datasetRS.init();

		this.showOverview = Boolean(this.stepViewConfig.showOverview);
		this.isStepLinear = this.stepViewConfig.isLinear(this);
		// take from beforeCreate
		// in first case take from dataset.resolver call
		this.datasetStepViewConfigs = this.datasetACS.getValueFromKeyPath('stepViewConfig.' + this.datasetACS.datasetCode);
		// if not in creation then load from dataset callModules
		if (!this.datasetStepViewConfigs){
			this.datasetStepViewConfigs = this.datasetACS.getValueFromKeyPath('domainContext.stepViewConfig.' + this.datasetACS.datasetCode);
		}
		
		this.buildSteps();

		this.datasetRS.record
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(this.onRecordChanged.bind(this));
		this.initAssistant();
	}

	ngOnDestroy(): void{
		if(this.assistantAnchorKey) this.assistantContextService.release(this.assistantAnchorKey);
		if(this.assistantAnchorKey) this.assistantContextService.setStep(null);
		super.ngOnDestroy();
	}

	/**
	 * Assistente dichiarato in config: area dello step aperto (IStepConfig.assistant) o della scheda
	 * (IDatasetConfig.assistant, mai in creazione). Dopo una conferma sul record della scheda: ricarica e avviso.
	 */
	private initAssistant(): void{
		this.assistantContextService.stepCommand
		.pipe(takeUntil(this._unsubscribeAll), filter(() => !!this.assistantAnchorKey))
		.subscribe(command => this.runAssistantCommand(command));

		this.assistantService.entityChanged
		.pipe(
			takeUntil(this._unsubscribeAll),
			filter(entity => !!this.assistantAnchorKey && entity.type === this.datasetACS.datasetCode && entity.id == this.datasetRS.recordId)
		)
		.subscribe(() => {
			// prima della ricarica: gli step pubblicano unsaved_changes nel loro snapshot (es. Descrizioni)
			const snapshot = this.assistantContextService.current().snapshot || {};
			const hadUnsavedChanges = Object.values(snapshot).some((value: any) => value?.unsaved_changes);
			this.datasetRS.reload();
			this.appService.showSuccessMessage(hadUnsavedChanges
				? 'Dati aggiornati dall\'assistente: le modifiche non salvate nel form sono state scartate.'
				: 'Dati aggiornati dall\'assistente.');
		});
	}

	/**
	 * Azioni lato client dell'assistente sulla scheda aperta: fill_form = valori nel form dello step aperto (nessun
	 * salvataggio); next_step = salva lo step se è un form (come "Salva e vai avanti": in creazione apre la modifica)
	 * altrimenti passa al successivo; go_to_step = apre lo step indicato senza salvare (il form aperto resta com'è).
	 */
	private runAssistantCommand(command: IAssistantClientAction): void{
		if(command.type === 'go_to_step'){
			if(command.index !== this.stepIndex && command.index >= 0 && command.index < (this.steps?.length ?? 0)) this.stepper.selectedIndex = command.index;
			// stepper lineare o indice non valido: lo step non cambia
			if(this.stepIndex !== command.index){
				this.appService.showErrorMessage('Apri lo step "' + command.title + '" e ripeti la richiesta all\'assistente.');
				this.assistantService.clientActionFailed();
			}
			return;
		}
		const formStep = this.formSteps?.find(step => step.stepIndex === this.stepIndex);
		if(command.type === 'fill_form'){
			if(!formStep?.formViewer){
				this.appService.showErrorMessage('Apri lo step con i campi da compilare e chiedi di nuovo all\'assistente.');
				return;
			}
			formStep.formViewer.formViewerService.setValues(command.values, { touch: true });
			this.appService.showSuccessMessage('Campi compilati dall\'assistente: controllali e salva.');
			return;
		}
		if(formStep){
			formStep.onSave({ fromAssistant: true });
			return;
		}
		if(this.stepIndex + 1 < (this.steps?.length ?? 0)) this.stepper.next();
		else this.assistantService.clientActionFailed();
	}

	/** step aperto per l'assistente: titolo e successivo (etichetta di "Salva e vai a ...") */
	private stepTitle(index: number): string | null{
		const title = this.computedSteps[index]?.title;
		if(!title) return null;
		return String(this.translateService.instant(title)).replace(/<[^>]*>/g, '').trim();
	}

	/** area dello step: onlyCreate vale in creazione, sul record salvato editArea */
	private stepArea(step: IStepConfig | undefined, isCreate: boolean): string | null{
		const assistant = step?.assistant;
		if(!assistant) return null;
		if(assistant.onlyCreate && !isCreate) return assistant.editArea || null;
		return assistant.area;
	}

	private syncAssistantAnchor(): void{
		const isCreate = this.datasetACS.actionCode === 'create';
		const stepAssistant = this.showOverview ? null : this.steps?.[this.stepIndex]?.assistant;
		const stepArea = this.showOverview ? null : this.stepArea(this.steps?.[this.stepIndex], isCreate);
		const datasetArea = isCreate ? null : this.datasetACS.datasetConfig?.assistant?.area;
		const area = stepArea || datasetArea || null;
		if(area !== this.assistantAnchoredArea){
			if(this.assistantAnchorKey) this.assistantContextService.release(this.assistantAnchorKey);
			this.assistantAnchorKey = area ? 'step-container:' + this.datasetACS.datasetCode + ':' + area : null;
			this.assistantAnchoredArea = area;
			if(area) this.assistantContextService.anchor(this.assistantAnchorKey, area);
		}
		if(isCreate && stepArea && stepAssistant.autoOpen && !this.assistantAutoOpened){
			this.assistantAutoOpened = true;
			this.assistantService.openWhenAvailable();
		}
		// dopo l'ancora: il cambio di step fa ripartire l'assistente ("continuiamo da qui"), che deve già avere l'area
		// (strumenti) dello step nuovo
		this.assistantContextService.setStep(area && !this.showOverview && this.steps?.length ? {
			index: this.stepIndex,
			count: this.steps.length,
			title: this.stepTitle(this.stepIndex) || '',
			next_title: this.stepTitle(this.stepIndex + 1),
			steps: (this.steps || [])
				.map((step, index) => ({ index, title: this.stepTitle(index) || '', area: this.stepArea(step, isCreate) }))
				.filter(step => step.area && step.title)
		} : null);
	}

	buildSteps(): void{
		const newStepConfigs = [];
		const newComputedSteps = [];
		for (const stepConfig of this.stepViewConfig.steps) {
			if(this.datasetStepViewConfigs && stepConfig.id && this.datasetStepViewConfigs[stepConfig.id]){
				const datasetStepConfig = this.datasetStepViewConfigs[stepConfig.id];
				//check config domains
				if(!datasetStepConfig.display) continue;
			}
			if(stepConfig.showIf && !stepConfig.showIf(this)) continue;
			newStepConfigs.push(stepConfig);
			newComputedSteps.push({
				config: stepConfig,
				title: stepConfig.stepperTitle || stepConfig.title,
				completed: this.computeStepCompleted(stepConfig),
			});
		}
		this.steps = newStepConfigs;
		this.computedSteps = newComputedSteps;
		if(this.tabGroup){
			this.tabGroup.selectedIndex = this.stepIndex;
		}
		this.syncAssistantAnchor();
	}

	computeStepCompleted(stepConfig: IStepConfig): boolean{
		if(this.showOverview) return false;
		if(!stepConfig.completed){
			return Boolean(this.record);
		}else{
			return stepConfig.completed(this);
		}
	}

	trackStepBy(index, item): any{
		return index;
	}

	onRecordChanged(record): void{
		this.isStepLinear = this.stepViewConfig.isLinear(this);
		if(!this.initStep){
			if(this.route.snapshot.queryParams['step']){
				this.showOverview = false;
				if(!this.isStepLinear){
					this.stepperIndex = Number(this.route.snapshot.queryParams['step']);
					// this.stepper.selectedIndex = this.route.snapshot.queryParams['step'];
				}else{
					this.stepperIndex = 0;
					// this.stepper.selectedIndex = 0;
				}
			}else{
				this.showOverview = true && this.stepViewConfig.showOverview;
			}
			if(!record) this.showOverview = false;
			
			this.initStep = true;
			this.stepIndex = this.stepperIndex;
			if(this.tabGroup) this.tabGroup.selectedIndex = this.stepperIndex;
		}
		if(this.stepViewConfig.subtitle){
			this.subtitle = this.stepViewConfig.subtitle(this);
		}
		this.buildSteps();
	}

	toggleSidebar(name): void{
		this._fuseSidebarService.getSidebar(name).toggleOpen();
	}

	stepChange(stepChange): void{
		this.showOverview = false;
		this.stepIndex = stepChange.selectedIndex;
		this.buildSteps();

		const urlTree = this.router.createUrlTree([], {
			queryParams: { step: this.stepIndex },
			queryParamsHandling: 'merge'
		});
		
		this.location.go(urlTree.toString());
	}

	onClickOverview(): void{
		this.showOverview = true;
		this.buildSteps();
		// this.stepper.selectedIndex = 0;

		const urlTree = this.router.createUrlTree([], {
			queryParams: { step: null },
			queryParamsHandling: 'merge'
		});
		
		this.location.go(urlTree.toString());
	}
}
